import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ICarrier,
  ICarrierSite,
  CreateCarrierDto,
  UpdateCarrierDto,
  UpdateCarrierSiteDto,
} from '@carrierbridge/shared';
import api from '@/lib/api';
import { bulkDeleteInBatches } from '@/lib/bulk-delete';

const CARRIERS_KEY = ['carriers'] as const;
// Clé de useTariffVersions — les mutations transporteur impactent aussi les
// grilles (cascade de suppression, compteur « grilles actives »).
const TARIFF_VERSIONS_KEY = ['tariff-versions'] as const;

export type CarrierListLifecycle = 'ACTIVE' | 'PROSPECT' | 'ALL';

/** Clé + queryFn partagées entre le hook liste et le prefetch au survol. */
export function carriersListQueryOptions(lifecycle: CarrierListLifecycle = 'ACTIVE') {
  return {
    queryKey: [...CARRIERS_KEY, 'lifecycle', lifecycle] as const,
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (lifecycle !== 'ACTIVE') params.lifecycle = lifecycle;
      const { data } = await api.get<ICarrier[]>('/carriers', { params });
      return data;
    },
  };
}

export function useCarriers(lifecycle: CarrierListLifecycle = 'ACTIVE') {
  return useQuery<ICarrier[]>(carriersListQueryOptions(lifecycle));
}

/**
 * Faits d'engagement dérivés par transporteur (état AO/Prod calculé côté API,
 * jamais un champ à maintenir). Map creux : pas d'entrée = aucun fait.
 */
export interface CarrierEngagement {
  activeGrids: number;
  openTenders: string[];
  recentOrders90d: number;
}

export type CarrierEngagementsMap = Record<string, CarrierEngagement>;

export function useCarrierEngagements(enabled = true) {
  return useQuery<CarrierEngagementsMap>({
    queryKey: [...CARRIERS_KEY, 'engagements'],
    queryFn: async () => {
      const { data } = await api.get<CarrierEngagementsMap>('/carriers/engagements');
      return data;
    },
    staleTime: 60_000,
    enabled,
  });
}

/**
 * Faits d'analyse du panel (statut contractuel dérivé, dépense et volumes
 * mesurés sur les expéditions importées). Même contrat que les engagements :
 * calculé côté API, jamais stocké, map creuse.
 */
export type GridValidityStatus = 'VALID' | 'FUTURE' | 'EXPIRED' | 'NONE';

export interface CarrierMonthlyPoint {
  month: string;
  spend: number;
  shipments: number;
}

export interface CarrierPanelFacts {
  gridStatus: GridValidityStatus;
  nextExpiryDays: number | null;
  nextExpiryDate: string | null;
  nextValidFrom: string | null;
  lastValidTo: string | null;
  gridCount: number;
  spend12m: number | null;
  shipments12m: number;
  weightKg12m: number | null;
  lastShipmentDate: string | null;
  monthly: CarrierMonthlyPoint[];
}

export interface UnmatchedCarrierCode {
  code: string;
  shipments: number;
  spend: number | null;
}

export interface CarrierPanelResponse {
  /** Fenêtre réellement analysée : les jeux importés sont historiques, elle
   *  part de la dernière expédition connue, pas d'aujourd'hui. */
  periodStart: string;
  periodEnd: string;
  facts: Record<string, CarrierPanelFacts>;
  unmatched: UnmatchedCarrierCode[];
}

/**
 * Normalise la réponse : l'écran affiche une liste, pas une erreur, si l'API
 * renvoie une charge partielle (déploiement en cours, ancienne version).
 * Défini au niveau module pour garder une identité stable — un `select`
 * anonyme se recalculerait à chaque rendu.
 */
function selectPanel(raw: Partial<CarrierPanelResponse> | null): CarrierPanelResponse {
  return {
    periodStart: raw?.periodStart ?? '',
    periodEnd: raw?.periodEnd ?? '',
    facts: raw?.facts ?? {},
    unmatched: raw?.unmatched ?? [],
  };
}

export function useCarrierPanel(enabled = true) {
  return useQuery({
    queryKey: [...CARRIERS_KEY, 'panel'],
    queryFn: async () => {
      const { data } = await api.get<CarrierPanelResponse>('/carriers/panel');
      return data;
    },
    select: selectPanel,
    staleTime: 60_000,
    enabled,
  });
}

export interface CarrierCorridor {
  origin: string;
  destination: string;
  shipments: number;
  spend: number | null;
  weightKg: number | null;
}

export interface CarrierShipmentProfile {
  periodStart: string;
  periodEnd: string;
  corridors: CarrierCorridor[];
  orderingParties: { label: string; shipments: number; spend: number | null }[];
}

/** Profil d'expéditions d'un transporteur — chargé seulement à l'ouverture du
 *  panneau de détail, pas pour toute la liste. */
export function useCarrierShipmentProfile(id: string | undefined) {
  return useQuery<CarrierShipmentProfile>({
    queryKey: [...CARRIERS_KEY, id, 'shipment-profile'],
    queryFn: async () => {
      const { data } = await api.get<CarrierShipmentProfile>(`/carriers/${id}/shipment-profile`);
      return data;
    },
    staleTime: 60_000,
    enabled: !!id,
  });
}

/** Clé + queryFn partagées entre le hook détail et le prefetch au survol. */
export function carrierDetailQueryOptions(id: string | undefined) {
  return {
    queryKey: [...CARRIERS_KEY, id] as const,
    queryFn: async () => {
      const { data } = await api.get<ICarrier>(`/carriers/${id}`);
      return data;
    },
  };
}

export function useCarrier(id: string | undefined) {
  return useQuery<ICarrier>({
    ...carrierDetailQueryOptions(id),
    enabled: !!id,
  });
}

export function useCreateCarrier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCarrierDto) => {
      const { data } = await api.post<ICarrier>('/carriers', dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
      // Compteurs de grilles dérivés de la liste des versions.
      queryClient.invalidateQueries({ queryKey: TARIFF_VERSIONS_KEY });
    },
  });
}

export function useUpdateCarrier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateCarrierDto }) => {
      const { data } = await api.patch<ICarrier>(`/carriers/${id}`, dto);
      return data;
    },
    // Optimistic update : bascule actif/inactif, scopes, renommage… se
    // reflètent immédiatement dans les listes et la fiche, rollback si erreur.
    onMutate: async ({ id, dto }) => {
      await queryClient.cancelQueries({ queryKey: CARRIERS_KEY });
      const prev = queryClient.getQueriesData({ queryKey: CARRIERS_KEY });
      // Listes (['carriers', 'lifecycle', …]) : patch de la ligne concernée.
      queryClient.setQueriesData<ICarrier[]>(
        { queryKey: [...CARRIERS_KEY, 'lifecycle'] },
        (old) => old?.map((c) => (c.id === id ? ({ ...c, ...dto } as ICarrier) : c)),
      );
      // Fiche détail (['carriers', id]), si déjà en cache.
      queryClient.setQueryData<ICarrier>([...CARRIERS_KEY, id], (old) =>
        old ? ({ ...old, ...dto } as ICarrier) : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      ctx?.prev.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
    },
  });
}

export function useUpdateCarrierSite(carrierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, dto }: { siteId: string; dto: UpdateCarrierSiteDto }) => {
      const { data } = await api.patch<ICarrierSite>(
        `/carriers/${carrierId}/sites/${siteId}`,
        dto,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...CARRIERS_KEY, carrierId] });
    },
  });
}

export interface RefreshFromWebsiteReport {
  website: string;
  matchedCompanyId: string | null;
  sitesAdded: number;
  sitesUpdated: number;
  sitesUnchanged: number;
  multipleMatches: boolean;
}

export function useRefreshCarrierFromWebsite(carrierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (websiteOverride?: string) => {
      const { data } = await api.post<{
        carrier: ICarrier;
        report: RefreshFromWebsiteReport;
      }>(`/carriers/${carrierId}/refresh-from-website`,
        websiteOverride ? { website: websiteOverride } : {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...CARRIERS_KEY, carrierId] });
    },
  });
}

export function useDeleteCarrierSite(carrierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (siteId: string) => {
      await api.delete(`/carriers/${carrierId}/sites/${siteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...CARRIERS_KEY, carrierId] });
    },
  });
}

export function useDeleteCarrier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/carriers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
      // La suppression cascade sur les grilles du transporteur : les listes
      // de versions (et le compteur « grilles actives ») doivent suivre.
      queryClient.invalidateQueries({ queryKey: TARIFF_VERSIONS_KEY });
    },
  });
}

/**
 * Suppression en masse depuis la console Data. Comme {@link useDeleteCarrier},
 * chaque transporteur emporte ses grilles : les listes de versions doivent
 * être invalidées en plus des listes de transporteurs.
 */
export function useBulkDeleteCarriers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      return bulkDeleteInBatches('/carriers', ids);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
      queryClient.invalidateQueries({ queryKey: TARIFF_VERSIONS_KEY });
    },
  });
}
